import {displayPropertyTitle, effectiveAvailability} from "@triple-h/shared";
import type { Property, SiteSettings } from "@triple-h/shared";

/**
 * Emergency fallback only. The public site always prefers `/api/v1/site`, and
 * the values that are present here are the ones already published by Triple H.
 * Optional channels stay empty rather than inventing contact details.
 */
export const fallbackSite: SiteSettings = {
  businessName: "Triple H Real Estate & Construction",
  tagline: "Real Estate with Integrity",
  publicPhone: "09 456 656 856",
  email: "triplehmyanmar.mm@gmail.com",
  viberUrl: "",
  viberChannelUrl: "",
  messengerUrl: "",
  canonicalUrl: "https://triplehmyanmar.com",
  timezone: "Asia/Yangon",
  defaultSeoDescription: "Trusted real estate and construction services in Myanmar."
};

export type CategoryVisual = {
  image: string;
  description: string;
};

export const categoryVisuals: Record<string, CategoryVisual> = {
  houses: {
    image: "/images/demo/category-house.webp",
    description: "Family homes and residential properties"
  },
  condos: {
    image: "/images/demo/category-condo.webp",
    description: "Apartments and multi-unit residences"
  },
  land: {
    image: "/images/demo/category-land.webp",
    description: "Plots, compounds and development land"
  },
  commercial: {
    image: "/images/demo/category-commercial.webp",
    description: "Offices, shops and business spaces"
  }
};

const categoryImageFallbacks = [
  "/images/demo/category-house.webp",
  "/images/demo/category-condo.webp",
  "/images/demo/category-land.webp",
  "/images/demo/category-commercial.webp"
];

export function categoryImage(slug: string, index = 0) {
  return categoryVisuals[slug]?.image || categoryImageFallbacks[index % categoryImageFallbacks.length];
}

export function categoryDescription(slug: string) {
  return categoryVisuals[slug]?.description || "Property listings managed by Triple H";
}

export function normalizePhone(phone: string) {
  const digits = phone.replace(/\D/g, "");
  return digits.startsWith("0") ? `95${digits.slice(1)}` : digits;
}

export function phoneHref(phone: string) {
  return `tel:${phone.replace(/[^+\d]/g, "")}`;
}

export function viberHref(site: SiteSettings) {
  return site.viberUrl;
}

export type Availability = {
  label: string;
  burmese: string;
  tone: "sale" | "sold" | "pending";
};

export function availability(property: Pick<Property, "soldOut" | "availabilityState" | "lastAvailabilityConfirmedAt">): Availability {
  if (property.soldOut) return { label: "Sold", burmese: "ရောင်းပြီး", tone: "sold" };
  if(effectiveAvailability(property)!=="available") return {label:"Confirm availability",burmese:"ရရှိနိုင်မှု မေးမြန်းပါ",tone:"pending"};
  return { label: "For Sale", burmese: "ရောင်းရန်", tone: "sale" };
}

/**
 * Some imported Facebook and archive listings were stored with a generic
 * title. Keep the stored title intact, but show the location as the human
 * heading on cards and property pages instead of repeating "SoldOut".
 */
export const propertyDisplayTitle = displayPropertyTitle;

export function plainText(html: string) {
  if (!html) return "";
  if (typeof document === "undefined") {
    return html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
  }
  const element = document.createElement("div");
  element.innerHTML = html;
  return (element.textContent || "").replace(/\s+/g, " ").trim();
}

export function contentExcerpt(property: Pick<Property, "contentHtml">, limit = 220) {
  return plainText(property.contentHtml).slice(0, limit);
}

const myanmarMonths = [
  "ဇန်နဝါရီ",
  "ဖေဖော်ဝါရီ",
  "မတ်",
  "ဧပြီ",
  "မေ",
  "ဇွန်",
  "ဇူလိုင်",
  "ဩဂုတ်",
  "စက်တင်ဘာ",
  "အောက်တိုဘာ",
  "နိုဝင်ဘာ",
  "ဒီဇင်ဘာ"
];

function myanmarDigits(value: string) {
  const digits = ["၀", "၁", "၂", "၃", "၄", "၅", "၆", "၇", "၈", "၉"];
  return value.replace(/\d/g, (digit) => digits[Number(digit)]);
}

export function formatMyanmarDate(value: string | null | undefined, includeDay = false) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const month = myanmarMonths[date.getMonth()];
  const year = myanmarDigits(String(date.getFullYear()));
  if (!includeDay) return `${month} ${year}`;
  return `${month} ${myanmarDigits(String(date.getDate()))}၊ ${year}`;
}

export function formatEnglishDate(value: string | null | undefined) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export function scrollToId(id: string) {
  const target = document.getElementById(id);
  if (!target) return;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  target.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
}
